import {useState} from 'react';
import {useForm} from 'hooks/common/useForm';
import {
    validateAboutMe,
    validateAddress,
    validateHourlyRate,
    validateLanguages,
    validateLanguageSkills,
} from './AccountEditTab/AboutMeTab/helpers';
import {validateCompanyInfo, validateCompanyAddressInfo} from './CompanyEditTab/CompanyTab/helpers';
import {isTeamMember, isTranslator} from 'constants/UserRoles';

const initialState = {
    info: {},
    experience: {},
    hourlyRate: {},
    language: {},
    company: {},
    companyAddress: {},
};

const useProfileEditForm = user => {
    const company = user.ownCompany || {};
    const isMember = isTeamMember(user);
    if (!isMember) initialState.address = {};
    const [errors, setErrors] = useState(initialState);

    const forms = {
        info: {
            form: useForm({
                values: {
                    firstName: user.firstName || '',
                    lastName: user.lastName || '',
                    birthday: (user.birthday && new Date(user.birthday)) || null,
                    workIndustry: user.workIndustry || null,
                    phone: user.phone || '',
                    timezone: user.timezone || null,
                    gender: user.gender || '',
                    nativeCountry: user.nativeCountry || null,
                    phoneCountryCode: user.phoneCountryCode || '',
                },
            }),
            validation: validateAboutMe,
        },

        address: {
            form: useForm({
                values: {
                    address: user.address || '',
                    city: user.city || null,
                    state: user.state || '',
                    postalCode: user.postalCode || '',
                    country: user.country || null,
                    house: user.house || '',
                    street: user.street || '',
                },
            }),
            validation: !isMember ? validateAddress : () => ({}),
        },

        hourlyRate: {
            form: useForm({
                values: {
                    costPerHour: user.costPerHour ?? 0,
                    currency: user.currency || null,
                    hourlyRate: user.hourlyRate || {},
                    full: user.full || null,
                    summary: user.summary || null,
                },
            }),
            validation: validateHourlyRate,
        },

        languages: {
            form: useForm({
                values: {
                    languages: user.languages || null,
                },
            }),
            validation: validateLanguages,
        },

        experience: {
            form: useForm({
                values: {
                    education: user.education || null,
                    employmentStatus: user.employmentStatus || null,
                    workExperience: user.workExperience || null,
                    company: user.company || '',
                    occupation: user.occupation || '',
                },
            }),
        },

        translate: {
            form: useForm({
                values: {
                    translateLanguages: user.translateLanguages || [],
                },
            }),
            validation: validateLanguageSkills,
        },

        consumer: {
            form: useForm({
                values: {
                    smartphoneBrand: user.smartphoneBrand,
                    smartphoneModel: user.smartphoneModel,
                    smartphoneColor: user.smartphoneColor,
                    tvBrand: user.tvBrand,
                    tvModel: user.tvModel,
                    cars: user.cars,
                    carModel: user.carModel,
                    brands: user.brands,
                    newspaper: user.newspaper,
                    newsChannels: user.newsChannels || [],
                    tvShows: user.tvShows,
                },
            }),
        },

        miscellaneous: {
            form: useForm({
                values: {
                    relationshipStatus: user.relationshipStatus || '',
                    availableForInterviews: Boolean(user.availableForInterviews),
                    about: user.about || '',
                },
            }),
        },

        companyAddress: {
            form: useForm({
                values: {
                    address: company.address || '',
                    city: company.city || '',
                    state: company.state || '',
                    postalCode: company.postalCode || '',
                    country: company.country || null,
                    house: company.house || '',
                    street: company.street || '',
                },
            }),
            validation: validateCompanyAddressInfo,
        },

        company: {
            form: useForm({
                values: {
                    name: company.name || '',
                    type: company.type || null,
                    vat: company.vat || '',
                    companyNumber: company.companyNumber || '',
                    businessRole: company.businessRole || null,
                },
            }),
            validation: validateCompanyInfo,
        },

        notifications: {
            form: useForm({values: user.notifications}),
        },
    };

    if (user.accountType !== 'company') {
        delete forms.company;
        delete forms.companyAddress;
    } else {
        delete forms.address;
    }

    if (!isTranslator(user.role)) {
        delete forms.language;
    }

    return {forms, errors, setErrors};
};

export default useProfileEditForm;
